import { format } from 'date-fns';

import type { DailySchedule, ScheduleSummary, TriggerDetail } from '@/types/schedule';

import { parseRecurringCronFormState } from './recurringCronSchedule';
import { computeNextCronExecutions } from './recurringCronValidation';
import { getBrowserTimezone, wallClockInTimeZoneToUtc } from './timezoneData';

export type ScheduleDateField = 'nextRun' | 'lastRun';
export type ScheduleTimeDisplayMode = 'local' | 'utc';

const API_DATE_TIME_PATTERN = /^(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i;

/**
 * Timezone the backend used when formatting nextRun/lastRun strings.
 * Mirrors transformer_api/triggerEvents.go (query timeZone vs stored repeating timeZone).
 */
export function resolveScheduleApiTimeZone(
  schedule: ScheduleSummary,
  field: ScheduleDateField,
  displayMode?: ScheduleTimeDisplayMode
): string {
  // lastRun is always formatted in the client's query timezone (getBrowserTimezone()),
  // independent of schedule type — see transformer_api/triggerEvents.go getTriggerSummary.
  if (field === 'lastRun') {
    return getBrowserTimezone();
  }

  if (schedule.cronExpression?.trim()) {
    const stored = schedule.cronTimeZone?.trim();
    if (stored) {
      return stored;
    }
    // Legacy null TZ: local toggle parses API strings in browser TZ; UTC toggle as UTC wall clock.
    if (displayMode === 'utc') {
      return 'UTC';
    }
    return getBrowserTimezone();
  }

  const storedDailyTz = schedule.dailySchedule?.timeZone?.trim();
  if (storedDailyTz) {
    return storedDailyTz;
  }

  const storedRepeatingTz = schedule.repeatingSchedule?.timeZone?.trim();
  if (storedRepeatingTz) {
    return storedRepeatingTz;
  }

  return getBrowserTimezone();
}

const MS_PER_DAY = 86_400_000;

/**
 * Next UTC instant for a daily schedule from stored timeUTC (HH:mm UTC wall clock).
 * The API nextRun calendar day varies with the query timeZone param and must not
 * be used when rebuilding the instant.
 */
export function computeDailyNextRunInstantFromTimeUtc(
  timeUTC: string,
  after: Date = new Date()
): Date | null {
  const [utcHStr, utcMStr] = timeUTC.split(':');
  const utcH = parseInt(utcHStr, 10);
  const utcM = parseInt(utcMStr, 10);
  if (Number.isNaN(utcH) || Number.isNaN(utcM)) {
    return null;
  }

  let candidate = new Date(
    Date.UTC(after.getUTCFullYear(), after.getUTCMonth(), after.getUTCDate(), utcH, utcM, 0, 0)
  );

  if (candidate.getTime() <= after.getTime()) {
    candidate = new Date(candidate.getTime() + MS_PER_DAY);
  }

  return candidate;
}

/** Rebuild daily nextRun from stored timeUTC; ignores query-timezone date in the API string. */
export function parseDailyNextRunInstant(_nextRun: string, daily: DailySchedule): Date | null {
  if (!daily.timeUTC?.trim()) {
    return null;
  }

  return computeDailyNextRunInstantFromTimeUtc(daily.timeUTC);
}

/**
 * Parse the trigger summary API datetime ("MM-DD-YYYY h:mm A") as wall-clock
 * time in the given IANA timezone — not browser local.
 */
export function parseApiDateTimeInTimeZone(value: string, timeZone: string): Date {
  const trimmed = value.trim();
  if (!trimmed) {
    return new Date(NaN);
  }

  const match = trimmed.match(API_DATE_TIME_PATTERN);
  if (!match) {
    return new Date(trimmed);
  }

  const [, mm, dd, yyyy, hStr, minStr, ampm] = match;
  const year = parseInt(yyyy, 10);
  if (year <= 1) {
    return new Date(NaN);
  }

  let hour = parseInt(hStr, 10);
  if (ampm.toUpperCase() === 'PM' && hour !== 12) {
    hour += 12;
  }
  if (ampm.toUpperCase() === 'AM' && hour === 12) {
    hour = 0;
  }

  return wallClockInTimeZoneToUtc(
    year,
    parseInt(mm, 10),
    parseInt(dd, 10),
    hour,
    parseInt(minStr, 10),
    timeZone
  );
}

/** Format a UTC instant as wall-clock time in an IANA timezone. */
export function formatInstantInTimeZone(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(instant);

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  const month = get('month');
  const day = get('day');
  const year = get('year');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod');

  return `${month} ${day}, ${year} ${hour}:${minute} ${dayPeriod}`;
}

function formatInstantUtc(instant: Date): string {
  const year = instant.getUTCFullYear();
  const month = instant.getUTCMonth();
  const day = instant.getUTCDate();
  const hours = instant.getUTCHours();
  const minutes = instant.getUTCMinutes();
  const utcAsLocal = new Date(year, month, day, hours, minutes);
  return `${format(utcAsLocal, 'MMM d, yyyy h:mm a')} UTC`;
}

/** Format a parsed API datetime instant for Local (browser) or UTC display. */
export function formatApiDateTimeInstant(instant: Date, mode: ScheduleTimeDisplayMode): string {
  if (isNaN(instant.getTime())) {
    return '-';
  }

  if (mode === 'utc') {
    return formatInstantUtc(instant);
  }

  return formatInstantInTimeZone(instant, getBrowserTimezone());
}

export function formatApiDateTimeInZone(
  value: string | undefined,
  apiTimeZone: string,
  mode: ScheduleTimeDisplayMode
): string {
  if (!value?.trim()) {
    return '-';
  }

  const instant = parseApiDateTimeInTimeZone(value, apiTimeZone);
  return formatApiDateTimeInstant(instant, mode);
}

export function findScheduleForDetail(
  detail: TriggerDetail,
  schedules: ScheduleSummary[]
): ScheduleSummary | undefined {
  return schedules.find(
    (schedule) =>
      String(schedule.baseId) === String(detail.baseId) &&
      String(schedule.environmentId) === String(detail.environmentId)
  );
}

/**
 * Timezone the backend used when formatting execution history rows.
 * Future repeating slots use stored schedule TZ; past runs use query/browser TZ.
 */
export function resolveExecutionApiTimeZone(
  detail: TriggerDetail,
  schedule: ScheduleSummary | undefined
): string {
  if (detail.executionStatus === 'Scheduled') {
    const storedDailyTz = schedule?.dailySchedule?.timeZone?.trim();
    if (storedDailyTz) {
      return storedDailyTz;
    }

    const storedRepeatingTz = schedule?.repeatingSchedule?.timeZone?.trim();
    if (storedRepeatingTz) {
      return storedRepeatingTz;
    }
  }

  return getBrowserTimezone();
}

/** UTC wall clock for cron + upcoming-schedule rows; schedule TZ for API daily/repeating scheduled rows. */
function resolveScheduledDetailApiTimeZone(
  detail: TriggerDetail,
  schedule: ScheduleSummary | undefined
): string {
  if (detail.executionStatus !== 'Scheduled' || detail.isUpcomingSchedule) {
    return 'UTC';
  }

  if (
    !schedule?.dailySchedule?.timeZone?.trim() &&
    !schedule?.repeatingSchedule?.timeZone?.trim()
  ) {
    return 'UTC';
  }

  return resolveExecutionApiTimeZone(detail, schedule);
}

export function formatTriggerDetailDateTime(
  detail: TriggerDetail,
  schedules: ScheduleSummary[],
  mode: ScheduleTimeDisplayMode
): string {
  const instant = parseTriggerDetailInstant(detail, schedules);
  if (!instant) {
    return '-';
  }

  return formatApiDateTimeInstant(instant, mode);
}

/**
 * Execution history always uses timeZone=UTC on getTriggerDetails and displays UTC.
 * Past ES rows and future cron slots are UTC wall clock; future repeating/daily rows
 * may use the schedule's stored timezone before conversion to UTC for display.
 */
export function parseExecutionHistoryInstant(
  detail: TriggerDetail,
  schedules: ScheduleSummary[]
): Date | null {
  const schedule = findScheduleForDetail(detail, schedules);

  // Summary-grid nextRun only — upcoming rows carry explicit UTC executionTime per slot.
  if (
    detail.executionStatus === 'Scheduled' &&
    schedule?.dailySchedule?.timeUTC &&
    !detail.isUpcomingSchedule
  ) {
    const instant = parseDailyNextRunInstant(detail.executionTime, schedule.dailySchedule);
    if (instant && !isNaN(instant.getTime())) {
      return instant;
    }
  }

  const apiTimeZone = resolveScheduledDetailApiTimeZone(detail, schedule);

  const instant = parseApiDateTimeInTimeZone(detail.executionTime, apiTimeZone);
  return isNaN(instant.getTime()) ? null : instant;
}

export function formatExecutionHistoryDateTime(
  detail: TriggerDetail,
  schedules: ScheduleSummary[]
): string {
  const instant = parseExecutionHistoryInstant(detail, schedules);
  if (!instant) {
    return '-';
  }

  return formatApiDateTimeInstant(instant, 'utc');
}

function isLocalWallClockWithinTimeFrame(
  localHour: number,
  localMinute: number,
  hoursFrom: number,
  hoursTo: number
): boolean {
  const total = localHour * 60 + localMinute;
  return total >= hoursFrom * 60 && total <= hoursTo * 60 + 59;
}

/**
 * Next AWS cron fire that falls inside the builder's local time-frame dropdown window.
 * Aligns grid "Next Run" (local) with frequency text for recurring crons with a window.
 */
export function computeCronNextRunInLocalTimeFrame(
  cronExpression: string,
  viewerTimeZone: string = getBrowserTimezone(),
  after: Date = new Date()
): Date | null {
  const parsed = parseRecurringCronFormState(cronExpression, viewerTimeZone);
  if (!parsed?.useTimeFrame) {
    return null;
  }

  const { hoursFrom, hoursTo } = parsed;
  const fires = computeNextCronExecutions(cronExpression, 200, viewerTimeZone, after);
  const next = fires.find((fire) =>
    isLocalWallClockWithinTimeFrame(fire.localHour, fire.localMinute, hoursFrom, hoursTo)
  );

  return next?.utc ?? null;
}

/**
 * Format a schedule summary nextRun/lastRun value for the grid toggle.
 * Parses using the backend format timezone, then:
 * - Local: browser timezone (when the run occurs for the viewer)
 * - UTC: universal time
 */
export function formatScheduleApiDateTime(
  value: string | undefined,
  schedule: ScheduleSummary,
  field: ScheduleDateField,
  mode: ScheduleTimeDisplayMode
): string {
  if (!value?.trim()) {
    return '-';
  }

  if (schedule.cronExpression?.trim()) {
    const instant = parseApiDateTimeInTimeZone(
      value,
      resolveScheduleApiTimeZone(schedule, field, mode)
    );
    return formatApiDateTimeInstant(instant, mode);
  }

  if (field === 'nextRun' && schedule.dailySchedule?.timeUTC) {
    const instant = parseDailyNextRunInstant(value, schedule.dailySchedule);
    if (instant && !isNaN(instant.getTime())) {
      return formatApiDateTimeInstant(instant, mode);
    }
  }

  const apiTimeZone = resolveScheduleApiTimeZone(schedule, field);
  return formatApiDateTimeInZone(value, apiTimeZone, mode);
}

/** Parse nextRun/lastRun to a Date for calendar placement. */
export function parseScheduleApiDateTime(
  value: string,
  schedule: ScheduleSummary,
  field: ScheduleDateField
): Date {
  if (schedule.cronExpression?.trim()) {
    return parseApiDateTimeInTimeZone(value, resolveScheduleApiTimeZone(schedule, field, 'local'));
  }

  if (field === 'nextRun' && schedule.dailySchedule?.timeUTC) {
    const instant = parseDailyNextRunInstant(value, schedule.dailySchedule);
    if (instant && !isNaN(instant.getTime())) {
      return instant;
    }
  }

  return parseApiDateTimeInTimeZone(value, resolveScheduleApiTimeZone(schedule, field));
}

/**
 * Map a UTC instant to a Date whose local components equal the UTC wall clock.
 * Used by react-big-calendar so event times render in UTC for all viewers.
 */
export function instantToUtcCalendarDate(instant: Date): Date {
  return new Date(
    instant.getUTCFullYear(),
    instant.getUTCMonth(),
    instant.getUTCDate(),
    instant.getUTCHours(),
    instant.getUTCMinutes(),
    instant.getUTCSeconds(),
    instant.getUTCMilliseconds()
  );
}

/** Inverse of instantToUtcCalendarDate — real UTC instant from a UTC-calendar Date. */
export function utcCalendarDateToInstant(calendarDate: Date): Date {
  return new Date(
    Date.UTC(
      calendarDate.getFullYear(),
      calendarDate.getMonth(),
      calendarDate.getDate(),
      calendarDate.getHours(),
      calendarDate.getMinutes(),
      calendarDate.getSeconds(),
      calendarDate.getMilliseconds()
    )
  );
}

/** Today's date on the UTC calendar (midnight UTC wall clock as local components). */
export function utcCalendarToday(): Date {
  const now = new Date();
  return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

/** Parse execution history / calendar detail row to a UTC instant. */
export function parseTriggerDetailInstant(
  detail: TriggerDetail,
  schedules: ScheduleSummary[]
): Date | null {
  const schedule = findScheduleForDetail(detail, schedules);

  if (
    detail.executionStatus === 'Scheduled' &&
    schedule?.dailySchedule?.timeUTC &&
    !detail.isUpcomingSchedule
  ) {
    const instant = parseDailyNextRunInstant(detail.executionTime, schedule.dailySchedule);
    if (instant && !isNaN(instant.getTime())) {
      return instant;
    }
  }

  const apiTimeZone =
    detail.executionStatus === 'Scheduled'
      ? resolveScheduledDetailApiTimeZone(detail, schedule)
      : resolveExecutionApiTimeZone(detail, schedule);

  const instant = parseApiDateTimeInTimeZone(detail.executionTime, apiTimeZone);

  return isNaN(instant.getTime()) ? null : instant;
}

/** Map a UTC instant to a react-big-calendar Date (local or UTC wall clock). */
export function instantToCalendarDisplayDate(instant: Date, mode: ScheduleTimeDisplayMode): Date {
  if (mode === 'utc') {
    return instantToUtcCalendarDate(instant);
  }

  // Browser-local wall clock as a fresh Date (never reuse the instant reference).
  return new Date(
    instant.getFullYear(),
    instant.getMonth(),
    instant.getDate(),
    instant.getHours(),
    instant.getMinutes(),
    instant.getSeconds(),
    instant.getMilliseconds()
  );
}

/**
 * Visible calendar range for getTriggerDetails API queries.
 * Sends wall-clock components (legacy dataProtect appends "Z" without true UTC conversion).
 */
export function calendarVisibleRangeToApiInstants(
  start: Date,
  endInclusive: Date
): { start: Date; end: Date } {
  return { start, end: endInclusive };
}

/** Keep the same calendar page when toggling Local ↔ UTC (not "today"). */
export function preserveCalendarDayOnModeSwitch(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
